<template>
    <div class="table-responsive">
        <loader v-if="isLoading" />
        <b-table-simple
            v-else
            borderless
            hover
            class="list"
            id="table-wellness-plans"
        >
            <b-thead>
                <b-tr>
                    <b-th>Name:</b-th>
                    <b-th>Joined:</b-th>
                    <b-th>Expires:</b-th>
                    <b-th>Status:</b-th>
                    <b-th>Invoice:</b-th>
                    <b-th width="30%">Notes:</b-th>
                    <b-th></b-th>
                </b-tr>
            </b-thead>
            <b-tbody>
                <b-tr v-if="list.length === 0">
                    <b-td colspan="7">
                        <i>No records available</i>
                    </b-td>
                </b-tr>
                <b-tr v-for="(record, i) in list" :key="record.id">
                    <b-td>
                        <input type="hidden" name="id" :value="record.id" />
                        <span v-if="record.template">
                            <TemplateLink
                                v-if="
                                    isBusinessOwner(record.template.business_id)
                                "
                                :temp="record.template"
                            />
                            <p v-else>
                                <b>{{ record.template.name }}</b>
                                ({{ record.template.business_name }})
                            </p>
                        </span>
                        <span v-else>--</span>
                    </b-td>
                    <b-td>
                        {{ _carbonDateDisplay(record.start_date) }}
                    </b-td>
                    <b-td>
                        {{ _carbonDateDisplay(record.end_date) }}
                    </b-td>
                    <b-td>
                        {{ showStatus(record.status) }}
                    </b-td>
                    <b-td>
                        <span
                            v-for="invoice in record.invoice"
                            :key="invoice.id"
                        >
                            <a
                                class="invoice-link"
                                :href="CI_APPS + invoice.ci_url"
                                v-if="isBusinessOwner(invoice.business_id)"
                            >
                                {{ invoice.name }}
                            </a>
                        </span>
                    </b-td>
                    <b-td>
                        {{ record.notes }}
                    </b-td>
                    <b-td class="text-right">
                        <div
                            v-if="
                                _hasAccess('patient.wellness.view') ||
                                _hasAccess('patient.wellness.cancel') ||
                                _hasAccess('patient.wellness.create')
                            "
                        ></div>
                        <spinner v-if="spinnerIndex == i" />
                        <b-dropdown
                            variant="link"
                            toggle-class="text-decoration-none"
                            no-caret
                            right
                            v-else
                            lazy
                        >
                            <template v-slot:button-content>
                                <i class="cus-cog-down"></i>
                            </template>

                            <b-dropdown-item
                                v-if="
                                    isTemplateOwner(record.template) &&
                                    _hasAccess('patient.wellness.edit') &&
                                    record.status == WELLNESS_PLAN.STATUS_ACTIVE
                                "
                                href="#"
                                @click.stop="editPlan(record)"
                            >
                                <i class="cus-page-white-edit"></i>
                                Edit Plan
                            </b-dropdown-item>

                            <b-dropdown-item
                                v-if="
                                    record.status !=
                                        WELLNESS_PLAN.STATUS_AUTO_RENEWED &&
                                    _hasAccess('patient.wellness.create') &&
                                    _isExtensionEnabled('WELLNESS') &&
                                    isTemplateOwner(record.template) &&
                                    isValidDurationLess30(record) &&
                                    statusActive
                                "
                                href="#"
                                @click.stop="renewPlan(record)"
                            >
                                <i class="cus-clipboard"></i>
                                Renew Plan
                            </b-dropdown-item>

                            <b-dropdown-item
                                href="#"
                                @click.stop="printSummary(record.id, i)"
                            >
                                <i class="cus-printer"></i>
                                Print Summary
                            </b-dropdown-item>

                            <b-dropdown-item
                                href="#"
                                @click.stop="printDetails(record.id, i)"
                            >
                                <i class="cus-printer"></i>
                                Print Details
                            </b-dropdown-item>

                            <b-dropdown-item
                                v-if="
                                    isTemplateOwner(record.template) &&
                                    _hasAccess('patient.wellness.cancel') &&
                                    record.status == WELLNESS_PLAN.STATUS_ACTIVE
                                "
                                href="#"
                                @click.stop="cancelPlan(record.id)"
                            >
                                <i class="cus-delete"></i>
                                Cancel Plan
                            </b-dropdown-item>
                        </b-dropdown>
                    </b-td>
                </b-tr>
            </b-tbody>
        </b-table-simple>

        <b-pagination
            v-if="showPagination"
            class="vet-pagination"
            align="right"
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            prev-text="Prev"
            next-text="Next"
            :hide-goto-end-buttons="true"
            @change="changePage"
        />

        <modal-cancel-wellness-plan
            ref="modalCancel"
            @on-success="onSuccessCancel"
        />
    </div>
</template>

<script>
import Spinner from "@/components/elements/spinner";
import Loader from "@/components/elements/loader-list";
import Axios from "axios";
import ModalCancelWellnessPlan from "@/components/patient-wellness-plans/wellness-modal-cancel";
import Patient from "@/services/patient.js";
import CONSTANTS from "@/constants/wellness_plan";

export default {
    name: "patientWellnessPlanList",
    components: {
        Spinner,
        Loader,
        ModalCancelWellnessPlan,
        TemplateLink: () => import("@/components/commons/template-link")
    },
    props: {
        type: {
            type: Number, // values : (0) Active, (1) Expired, (2) Cancelled, (3) Auto Renewed
            default: 0
        },
        patientId: {
            type: String
        }
    },
    data() {
        return {
            isLoading: true,
            list: [],
            spinnerIndex: -1,
            showPagination: false,
            currentPage: 1,
            totalRows: 0,
            perPage: 10,
            WELLNESS_PLAN: CONSTANTS.WELLNESS_PLAN
        };
    },
    computed: {
        statusActive() {
            return this.$store.getters["patient/statusActive"];
        },
        CI_APPS() {
            return process.env.CI_APPS;
        }
    },
    mounted() {
        this.getList(this.type);
    },
    methods: {
        changePage(nextPage) {
            this.getList(this.type, nextPage);
        },
        isBusinessOwner(businessId) {
            return businessId === this._setting.business.id;
        },
        async getList(type, page = 1) {
            try {
                this.isLoading = true;

                let params = {
                    status: type,
                    page: page
                };

                const response = await Patient.getWellnessPlanList(
                    this.patientId,
                    params
                );

                if (
                    response &&
                    response.message &&
                    response.message.code === 200
                ) {
                    this.isLoading = false;
                    this.list = response.resources;

                    // update pagination details
                    this.currentPage = response.meta.currentPage;
                    this.totalRows = response.meta.total;
                    this.perPage = response.meta.perPage;
                    this.showPagination = response.meta.lastPage > 1;
                } else {
                    throw new Error("Unable to fetch list.");
                }
            } catch (e) {
                this.isLoading = false;
                this.list = [];
            }
        },
        showStatus(type) {
            const mapping = {
                0: "Active",
                1: "Expired",
                2: "Cancelled",
                3: "Auto-Renewed"
            };

            return mapping[type] || "-";
        },
        editPlan(record) {
            this.$store.commit("patient/openModalEnrollInPlan", {
                action: "edit",
                record: {
                    id: record.id,
                    enrolled_by: record.enrolled_by,
                    provider_id: record.provider_id,
                    is_auto_renew: record.is_auto_renew,
                    start_date: record.start_date,
                    patient_id: record.patient_id,
                    payment_method_profile_id: record.payment_method_profile_id,
                    charge_day: record.charge_day,
                    notes: record.notes,
                    template: record.template,
                    template_id: record.template_id,
                    default_provider: record.default_provider
                }
            });
        },
        printSummary(recordId, index) {
            this.spinnerIndex = index;

            Patient.printWellnessPlanSummary(recordId)
                .then(response => {
                    this.spinnerIndex = -1;
                    if (response.message.code == 200) {
                        this._message("Opening PDF");
                        if (response.resources.path) {
                            setTimeout(function () {
                                window.open(response.resources.path);
                            }, 1000);
                        }
                    } else {
                        this._message(
                            "Error : Unable to print wellness plan summary",
                            "danger"
                        );
                    }
                })
                .catch(error => {
                    this.spinnerIndex = -1;
                    this._message(
                        "Error : Unable to print wellness plan summary",
                        "danger"
                    );
                    console.error(error);
                });
        },
        printDetails(recordId, index) {
            this.spinnerIndex = index;

            Patient.printWellnessPlanDetails(recordId)
                .then(response => {
                    this.spinnerIndex = -1;

                    if (response.code === 200) {
                        this._message("Opening PDF");
                        if (response.resources.path) {
                            setTimeout(function () {
                                window.open(response.resources.path);
                            }, 1000);
                        }
                    } else {
                        this._message(
                            "Error : Unable to print wellness plan details",
                            "danger"
                        );
                    }
                })
                .catch(error => {
                    this.spinnerIndex = -1;
                    this._message(
                        "Error : Unable to print wellness plan details",
                        "danger"
                    );
                    console.error(error);
                });
        },
        cancelPlan(recordId) {
            this.$refs.modalCancel.open(recordId);
        },
        onSuccessCancel() {
            this.getList(this.type);
        },
        isTemplateOwner(template) {
            if (template) {
                if (template.business_id == this._setting.business.id) {
                    return true;
                }
            }

            return false;
        },
        isValidDurationLess30(record) {
            if (record.end_date) {
                let now = this._now();
                let endDate = this._fromDateCarbon(record.end_date);
                let duration = endDate.diff(now, ["days"]).toObject();

                return duration && duration.days < 30;
            }

            return false;
        },
        renewPlan(record) {
            this.$store.commit("patient/openModalEnrollInPlan", {
                action: "add",
                record: {
                    id: record.id,
                    enrolled_by: record.enrolled_by,
                    provider_id: record.provider_id,
                    is_auto_renew: record.is_auto_renew,
                    start_date: record.start_date,
                    patient_id: record.patient_id,
                    payment_method_profile_id: record.payment_method_profile_id,
                    charge_day: record.charge_day,
                    notes: record.notes,
                    template: record.template,
                    template_id: record.template_id
                }
            });
        }
    }
};
</script>

<style scoped>
.invoice-link {
    display: inline-block;
}
</style>
